import { getTheme } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPPrimaryButton, IPSpinner, IPText } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { globalStackTokensGapSmall, globalTextStylesBold } from 'globalStyles';
import ResourceLink from 'models/resourceLink';
import { UserReadConfirmation } from 'models/userReadConfirmation';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiUpdateReadConfirmation } from 'services/Api/linkService';
import { apiRequest } from 'services/Auth/authConfig';
import logger from 'services/Logging/logService';
import { toLocaleDateTimeShort } from 'utils/datetime';

export interface ILinkPreviewProps {
  link: ResourceLink;
  version: string;
  onConfirmed: (usr: UserReadConfirmation) => void;
  onNext: () => void;
  hasNext: boolean;
}

export const LinkPreviewConfirmation = (props: ILinkPreviewProps) => {
  const appContext = useContext(AppContext);
  const { t } = useTranslation(['translation', 'publication']);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [userReadConfirmation, setUserReadConfirmation] = useState<UserReadConfirmation | undefined>(
    props.link.getRC(),
  );
  const theme = getTheme();
  const frameHeight: number = 56;

  useEffect(() => {
    setUserReadConfirmation(props.link.getRC());
  }, [props.link]);

  const onConfirm = async () => {
    try {
      if (isLoading) return;
      setIsLoading(true);
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const usr = await apiUpdateReadConfirmation(
        props.link.linkId,
        props.version,
        accessToken,
        appContext.globalDataCache,
      );
      setUserReadConfirmation(usr);
      props.onConfirmed(usr);
    } catch (err) {
      logger.debug('Read confirmation error', err);
      appContext.showNotification(t('publication:ReadConfirmation.ConfirmError'), true);
    } finally {
      setIsLoading(false);
    }
  };

  //
  // Main render
  //
  if (userReadConfirmation?.readConfirmed) {
    return (
      <IPStack
        horizontal
        verticalAlign="center"
        horizontalAlign="space-between"
        styles={{
          root: {
            height: frameHeight,
            border: '1px solid green',
            borderRadius: '3px',
            padding: 10,
            background: theme.palette.themeLighter,
          },
        }}
        tokens={globalStackTokensGapSmall}
      >
        <IPStack.Item>
          <IPText styles={globalTextStylesBold}>
            {t('publication:ReadConfirmation.InfoConfirmed', {
              version: userReadConfirmation?.version,
              date: toLocaleDateTimeShort(userReadConfirmation?.readConfirmed),
            })}
          </IPText>
        </IPStack.Item>
        {props.hasNext && (
          <IPStack.Item>
            <IPPrimaryButton disabled={isLoading} onClick={props.onNext}>
              {t('publication:ReadConfirmation.NextButton')}
            </IPPrimaryButton>
          </IPStack.Item>
        )}
      </IPStack>
    );
  }

  if (!userReadConfirmation || !props.version) {
    return null;
  }

  return (
    <IPStack
      horizontal
      verticalAlign="center"
      horizontalAlign="space-between"
      styles={{
        root: {
          height: frameHeight,
          border: '1px solid red',
          borderRadius: '3px',
          padding: 10,
          background: appContext.useDarkMode ? theme.palette.themeDarker : theme.palette.themeLighter,
        },
      }}
      tokens={globalStackTokensGapSmall}
    >
      <IPStack.Item>
        <IPText styles={globalTextStylesBold}>{t('publication:ReadConfirmation.Info')}</IPText>
      </IPStack.Item>
      <IPStack.Item>
        {!isLoading && (
          <IPPrimaryButton
            text={t('publication:ReadConfirmation.ConfirmButton')}
            disabled={isLoading}
            onClick={onConfirm}
          ></IPPrimaryButton>
        )}
        {isLoading && (
          <IPStack horizontal verticalAlign="center" tokens={globalStackTokensGapSmall}>
            <IPText>{t('translation:General.Notifications.Saving')}</IPText>
            <IPSpinner />
          </IPStack>
        )}
      </IPStack.Item>
    </IPStack>
  );
};
